import { useState, useEffect } from "react";
import config from "../config";

const API_BASE = `${config.API_BASE_URL}/Contact`;

const statusBadge = (approve) => {
  if (approve === null || approve === undefined)
    return { label: "Pending", color: "#f59e0b", bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.3)" };
  if (approve === "" || approve === false || approve === "false")
    return { label: "Rejected", color: "#ef4444", bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.3)" };
  return { label: "Approved", color: "#22c55e", bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.3)" };
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ReviewContact() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [emailBody, setEmailBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error("Failed to fetch contacts");
      const data = await res.json();
      setContacts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = async (email, name) => {
    if (!window.confirm(`Delete contact from ${name}?`)) return;
    setDeletingId(email);
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(email)}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setContacts((prev) => prev.filter((c) => c.email !== email));
        showToast(`Deleted contact from ${name}`);
      } else {
        setContacts((prev) => prev.filter((c) => c.email !== email));
        showToast(`Deleted contact from ${name}`);
      }
    } catch {
      setContacts((prev) => prev.filter((c) => c.email !== email));
      showToast(`Deleted contact from ${name}`);
    } finally {
      setDeletingId(null);
    }
  };

  const openEmailModal = (contact) => {
    setEmailModal(contact);
    setEmailSubject(`Re: Your message - ${contact.name}`);
    setEmailBody(
      `Hi ${contact.name},\n\nThank you for reaching out!\n\nRegarding your message:\n"${contact.message}"\n\nBest regards,\nThe Team`
    );
  };

const handleSendEmail = async () => {
  if (!emailModal?.email) {
    showToast("No email address found", "error");
    return;
  }

  setSending(true);
  try {
    const payload = {
      toEmail: emailModal.email,
      toName: emailModal.name ?? "",
      subject: emailSubject,
      body: emailBody,
    };

    // ← Check mo ito sa console para makita kung tama data
    console.log("📧 Sending payload:", payload);

    const res = await fetch(`${config.API_BASE_URL}/Email/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    console.log("📬 Response:", data);

    if (!res.ok) throw new Error(data.error || "Failed to send email");

    showToast(`Email sent to ${emailModal.email}!`);
    setEmailModal(null);
  } catch (err) {
    console.error("❌ Email error:", err);
    showToast(err.message || "Failed to send email", "error");
  } finally {
    setSending(false);
  }
};
  return (
    <div style={styles.root}>
      {/* Toast */}
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "error" ? "#ef4444" : "#4f46e5" }}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Contact Inbox</h1>
          <p style={styles.subtitle}>Manage and respond to incoming messages</p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchContacts}>
          <span style={{ fontSize: 16 }}>↻</span> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        {[
          { label: "Total", value: contacts.length, color: "#6366f1" },
          { label: "Pending", value: contacts.filter((c) => c.approve === null).length, color: "#f59e0b" },
          { label: "Approved", value: contacts.filter((c) => c.approve && c.approve !== "" && c.approve !== "false").length, color: "#22c55e" },
        ].map((s) => (
          <div key={s.label} style={styles.statCard}>
            <span style={{ ...styles.statNum, color: s.color }}>{s.value}</span>
            <span style={styles.statLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={styles.center}>
          <div style={styles.spinner} />
          <p style={{ color: "#94a3b8", marginTop: 16 }}>Loading contacts...</p>
        </div>
      ) : error ? (
        <div style={styles.center}>
          <p style={{ color: "#ef4444", fontSize: 15 }}>⚠ {error}</p>
          <button style={styles.refreshBtn} onClick={fetchContacts}>Try Again</button>
        </div>
      ) : contacts.length === 0 ? (
        <div style={styles.center}>
          <p style={{ color: "#94a3b8", fontSize: 15 }}>No contacts found.</p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Name", "Email", "Message", "Date", "Status", "Actions"].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, i) => {
                const badge = statusBadge(c.approve);
                const isDeleting = deletingId === c.email;
                return (
                  <tr key={i} style={styles.tr} className="contact-row">
                    <td style={styles.td}>
                      <div style={styles.avatar}>{c.name?.[0]?.toUpperCase() || "?"}</div>
                      <span style={styles.nameText}>{c.name}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.emailText}>{c.email}</span>
                    </td>
                    <td style={{ ...styles.td, maxWidth: 220 }}>
                      <span style={styles.msgText}>{c.message}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.dateText}>{formatDate(c.createdAt)}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        color: badge.color,
                        background: badge.bg,
                        border: `1px solid ${badge.border}`,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.emailBtn}
                          onClick={() => openEmailModal(c)}
                          title="Email this contact"
                        >
                          ✉ Email
                        </button>
                        <button
                          style={{ ...styles.deleteBtn, opacity: isDeleting ? 0.5 : 1 }}
                          onClick={() => handleDelete(c.email, c.name)}
                          disabled={isDeleting}
                          title="Delete contact"
                        >
                          {isDeleting ? "..." : "✕ Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && setEmailModal(null)}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>✉ Send Email</h2>
              <button style={styles.closeBtn} onClick={() => setEmailModal(null)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.field}>
                <label style={styles.label}>To</label>
                <input style={styles.input} value={emailModal.email} readOnly />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Subject</label>
                <input
                  style={styles.input}
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Message</label>
                <textarea
                  style={{ ...styles.input, height: 160, resize: "vertical" }}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                />
              </div>
              <div style={styles.originalMsg}>
                <span style={{ color: "#64748b", fontSize: 12, fontFamily: "monospace" }}>
                  Original: "{emailModal.message}"
                </span>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setEmailModal(null)}>Cancel</button>
              <button style={{ ...styles.sendBtn, opacity: sending ? 0.7 : 1 }} onClick={handleSendEmail} disabled={sending}>
                {sending ? "Sending..." : "Send Email ✉"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@400;600;700;800&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .contact-row:hover td { background: rgba(99,102,241,0.06) !important; }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}

const styles = {
  root: {
    fontFamily: "'Syne', sans-serif",
    padding: "28px 24px",
    color: "#e2e8f0",
    position: "relative",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    background: "linear-gradient(135deg, #6366f1, #818cf8)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 400,
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.3)",
    borderRadius: 8,
    color: "#818cf8",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Syne', sans-serif",
    transition: "all 0.2s",
  },
  statsRow: {
    display: "flex",
    gap: 14,
    marginBottom: 24,
  },
  statCard: {
    flex: 1,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  statNum: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  tableWrapper: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 14,
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    padding: "13px 18px",
    textAlign: "left",
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    transition: "background 0.15s",
  },
  td: {
    padding: "13px 18px",
    verticalAlign: "middle",
    display: "revert",
  },
  avatar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    marginRight: 10,
    verticalAlign: "middle",
  },
  nameText: {
    fontWeight: 600,
    color: "#e2e8f0",
    verticalAlign: "middle",
  },
  emailText: {
    color: "#94a3b8",
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
  },
  msgText: {
    color: "#94a3b8",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.5,
  },
  dateText: {
    color: "#64748b",
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  badge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "nowrap",
  },
  emailBtn: {
    padding: "6px 12px",
    borderRadius: 7,
    border: "1px solid rgba(99,102,241,0.35)",
    background: "rgba(99,102,241,0.1)",
    color: "#818cf8",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Syne', sans-serif",
    whiteSpace: "nowrap",
    transition: "all 0.15s",
  },
  deleteBtn: {
    padding: "6px 12px",
    borderRadius: 7,
    border: "1px solid rgba(239,68,68,0.3)",
    background: "rgba(239,68,68,0.08)",
    color: "#f87171",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Syne', sans-serif",
    whiteSpace: "nowrap",
    transition: "all 0.15s",
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 0",
    gap: 16,
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid rgba(99,102,241,0.2)",
    borderTop: "3px solid #6366f1",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 24,
  },
  modal: {
    background: "#111827",
    border: "1px solid rgba(99,102,241,0.2)",
    borderRadius: 18,
    width: "100%",
    maxWidth: 520,
    boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: "#e2e8f0",
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: 18,
    cursor: "pointer",
    padding: 4,
    lineHeight: 1,
  },
  modalBody: {
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  input: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 9,
    padding: "10px 14px",
    color: "#e2e8f0",
    fontSize: 14,
    fontFamily: "'Syne', sans-serif",
    outline: "none",
    width: "100%",
  },
  originalMsg: {
    background: "rgba(99,102,241,0.05)",
    border: "1px solid rgba(99,102,241,0.15)",
    borderRadius: 8,
    padding: "10px 14px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: "16px 24px",
    borderTop: "1px solid rgba(255,255,255,0.07)",
  },
  cancelBtn: {
    padding: "9px 20px",
    borderRadius: 9,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "transparent",
    color: "#94a3b8",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Syne', sans-serif",
  },
  sendBtn: {
    padding: "9px 22px",
    borderRadius: 9,
    border: "none",
    background: "linear-gradient(135deg, #4f46e5, #6366f1)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Syne', sans-serif",
    boxShadow: "0 4px 14px rgba(79,70,229,0.4)",
  },
  toast: {
    position: "fixed",
    top: 24,
    right: 24,
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    zIndex: 9999,
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    animation: "slideIn 0.3s ease",
  },
};